// packages/core/src/schedule/step.ts
//
// Changement de pas (D8b) : réaligne les plages existantes sur les bornes du
// nouveau pas, VERS L'EXTÉRIEUR (12:45-13:30 en pas 60 → 12:00-14:00), sans
// jamais les faire disparaître ni les rétrécir. Deux plages réalignées qui se
// retrouvent contiguës ou chevauchantes sont réunies en une seule (l'exemple
// de la spec : « 12:00-12:45 → 12:00-13:00 ; 12:45-13:30 → 12:00-14:00 ;
// réunies en 12:00-14:00 »). Ce module ne décide PAS s'il faut demander
// confirmation — il calcule juste le résultat et la liste des changements,
// pour que l'appelant (composant) affiche le dialogue à 3 boutons (D8b) et
// applique ou non le résultat.

import { DAY_MINUTES, type DayRangesMap, type Range } from "./types";

function floorToStep(minute: number, step: number): number {
  return Math.floor(minute / step) * step;
}
function ceilToStep(minute: number, step: number): number {
  return Math.ceil(minute / step) * step;
}

export interface RangeChange {
  before: Range;
  /** La plage réalignée avant réunion avec ses voisines. */
  after: Range;
}

export interface RealignResult {
  /** Les plages réalignées et réunies, triées. */
  ranges: Range[];
  /** Au moins une plage a changé de bornes (→ dialogue de confirmation, D8b). */
  changed: boolean;
  /** Détail par plage d'origine, pour le message du dialogue. */
  changes: RangeChange[];
}

/**
 * Réaligne les plages d'UN jour sur le nouveau pas, vers l'extérieur, puis
 * réunit celles qui se chevauchent ou se touchent après réalignement.
 */
export function realignDayForStep(
  ranges: Range[],
  newStep: number,
): RealignResult {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const changes: RangeChange[] = sorted.map((before) => {
    const start = Math.max(0, floorToStep(before.start, newStep));
    const end = Math.min(DAY_MINUTES, ceilToStep(before.end, newStep));
    return { before, after: { start, end } };
  });

  // Réunion des plages réalignées qui se touchent/chevauchent désormais.
  const merged: Range[] = [];
  for (const { after } of changes) {
    const last = merged[merged.length - 1];
    if (last && after.start <= last.end) {
      last.end = Math.max(last.end, after.end);
    } else {
      merged.push({ ...after });
    }
  }

  const changed = changes.some(
    (c) => c.before.start !== c.after.start || c.before.end !== c.after.end,
  );

  return { ranges: merged, changed, changes };
}

/** `realignDayForStep` sur toutes les dates d'un `DayRangesMap`. */
export function realignForStep(
  dayRanges: DayRangesMap,
  newStep: number,
): {
  ranges: DayRangesMap;
  changed: boolean;
  changesByDate: Record<string, RangeChange[]>;
} {
  const out: DayRangesMap = {};
  const changesByDate: Record<string, RangeChange[]> = {};
  let changed = false;
  for (const date of Object.keys(dayRanges)) {
    const result = realignDayForStep(dayRanges[date], newStep);
    out[date] = result.ranges;
    if (result.changes.length > 0) changesByDate[date] = result.changes;
    if (result.changed) changed = true;
  }
  return { ranges: out, changed, changesByDate };
}
